import { useMemo } from "react";
import { Info } from "lucide-react";

import Bandeira from "../Bandeira";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import usePaisFiscal, {
	mascararDocumento,
	regrasVigentes,
} from "../../hooks/usePaisFiscal";
import { cn } from "../../lib/utils";

// Campo do documento fiscal, com saída para o cliente estrangeiro.
//
// O normal é o documento do país da plataforma — cobrando em euro, NIF; em
// real, CPF/CNPJ. Mas quem atende os dois mercados cadastra o cliente de fora
// de vez em quando, e antes disto aqui não conseguia: o formulário recusava o
// CPF de um cliente brasileiro numa operação portuguesa, sem dizer por quê.
//
// Ligar a chave revela o seletor de documento. Cada opção vem com uma frase
// explicando o que é — "NUIT" e "CNPJ" não significam nada para quem é de
// fora, e escolher errado só apareceria lá na frente, quando a cobrança
// falhasse.
//
// O país escolhido é gravado junto do cadastro. Sem isso, reabrir a ficha de
// um cliente estrangeiro faria a tela conferir o documento pelas regras
// erradas e recusar o que ela mesma salvou.

const DocumentoFiscal = ({
	id = "document",
	paisSelecionado,
	onPaisChange,
	valor,
	onValorChange,
	erro,
	className,
}) => {
	const plataforma = usePaisFiscal();

	// Derivada dentro do useMemo, e não numa variável solta: `|| []` cria um
	// array novo a cada render e faria a memorização se refazer sempre.
	const opcoes = useMemo(() => plataforma.opcoes || [], [plataforma]);

	const estrangeiro = Boolean(paisSelecionado);

	// Regras em vigor: as do país escolhido à mão, ou as da plataforma.
	const regras = useMemo(
		() => regrasVigentes(plataforma, paisSelecionado),
		[paisSelecionado, plataforma],
	);

	const alternar = (ligado) => {
		// Desligar volta ao documento da plataforma. O valor digitado é mantido:
		// apagá-lo puniria quem só queria conferir o que a chave fazia.
		if (!ligado) {
			onPaisChange("");
			return;
		}

		// Começa por um país diferente do da plataforma — ligar a chave e
		// continuar vendo o mesmo rótulo pareceria que nada aconteceu.
		const primeiro =
			opcoes.find((o) => o.pais !== plataforma.pais)?.pais || plataforma.pais;

		onPaisChange(primeiro);
	};

	return (
		<div className={cn("space-y-1.5", className)}>
			<Label htmlFor={id}>{regras.rotuloDocumento}</Label>

			<Input
				id={id}
				name={id}
				inputMode="numeric"
				placeholder={regras.exemploDocumento}
				// Mascarado também na leitura: o que vem do banco são só dígitos, e
				// abrir uma ficha com "12345678901" no lugar do CPF formatado
				// parece cadastro corrompido.
				value={mascararDocumento(valor || "", regras)}
				onChange={(e) => onValorChange(mascararDocumento(e.target.value, regras))}
			/>

			{erro && <p className="text-xs text-destructive">{erro}</p>}

			<div className="flex items-center gap-2 pt-1">
				<Switch
					id={`${id}-estrangeiro`}
					checked={estrangeiro}
					onCheckedChange={alternar}
				/>
				<Label
					htmlFor={`${id}-estrangeiro`}
					className="cursor-pointer text-xs font-normal text-muted-foreground"
				>
					Cliente estrangeiro — documento de outro país
				</Label>
			</div>

			{estrangeiro && (
				<div className="space-y-1.5 rounded-md border bg-muted/40 p-2.5">
					<Select value={paisSelecionado} onValueChange={onPaisChange}>
						<SelectTrigger id={`${id}-pais`} className="bg-background">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{opcoes.map((o) => (
								<SelectItem key={o.pais} value={o.pais}>
									<span className="flex items-center gap-2">
										<Bandeira codigo={o.pais} />
										{o.nome} — {o.rotuloDocumento}
									</span>
								</SelectItem>
							))}
						</SelectContent>
					</Select>

					<p className="flex gap-1.5 text-xs leading-relaxed text-muted-foreground">
						<Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
						{regras.descricaoDocumento}
					</p>
				</div>
			)}
		</div>
	);
};

export default DocumentoFiscal;
